import React, { useState, useEffect } from "react";
import { Container, Form, Row, Col } from "react-bootstrap";
import { ToastContainer, toast } from "react-toastify";
import "./UnifiedFormStyles.css";
import AppButton from "../../Components/MerchantButton";
import { compressAndUpload } from "../../Helpers/Utils/photoUpload";

function AwardingMerienda({ navigation, primaryDetails, setPrimaryDetails }) {
  const [isError, setIsError] = useState({
    merienda_purchase_receipt: false,
    amount: false,
    food_item: false,
    or_no: false,
    receiver_name: false,
    receiver_position: false,
    merienda_certificate: false,
  });

    const [isUploading, setIsUploading] = useState(false);

  const handlePhotoUpload = async (e, fieldName) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      toast.error("Photo exceeds 5MB limit. Please retake the photo.", {
        autoClose: 3000, hideProgressBar: true, theme: "colored"
      });
      e.target.value = '';
      return;
    }

    setIsUploading(true);
    try {
      const uploadResult = await compressAndUpload(file);
      if (!uploadResult.success) {
        toast.error(uploadResult.error || "Upload failed. Please try again.");
        e.target.value = '';
        return;
      }
      setPrimaryDetails(prev => ({
        ...prev,
        [`${fieldName}_temp_path`]: uploadResult.temp_path
      }));
      if (isError[fieldName]) {
        setIsError(prev => ({ ...prev, [fieldName]: false }));
      }
    } catch (error) {
      toast.error("Error uploading photo");
    } finally {
      setIsUploading(false);
    }
  };

  // Sync to primaryDetails whenever state changes
  useEffect(() => {
    setPrimaryDetails((prev) => ({
      ...prev,
    }));
  }, [setPrimaryDetails]);

  const handleInputChange = (e) => {
    const { name, value, type } = e.target;
    if (type === "file") return; // photos handled by handlePhotoUpload
    setPrimaryDetails(prev => ({ ...prev,
      [name]: value,
    }));
    if (isError[name]) {
      setIsError({
        ...isError,
        [name]: false,
      });
    }
  };

  const validateForm = () => {
    const errors = {
      merienda_purchase_receipt: !primaryDetails.merienda_purchase_receipt_temp_path,
      amount: !primaryDetails.amount,
      food_item: !primaryDetails.food_item,
      or_no: !primaryDetails.or_no,
      receiver_name: !primaryDetails.receiver_name,
      receiver_position: !primaryDetails.receiver_position,
      merienda_certificate: !primaryDetails.merienda_certificate_temp_path,
    };

    setIsError(errors);

    const hasErrors = Object.values(errors).some((error) => error);
    if (hasErrors) {
      toast.error("Please fill in all required fields.", {
        autoClose: 3000,
        hideProgressBar: true,
        theme: "colored",
      });
      return false;
    }
    return true;
  };

  const isFormValid = () => {
    return !!primaryDetails.merienda_purchase_receipt_temp_path &&
      !!primaryDetails.amount &&
      !!primaryDetails.food_item &&
      !!primaryDetails.or_no &&
      !!primaryDetails.receiver_name &&
      !!primaryDetails.receiver_position &&
      !!primaryDetails.merienda_certificate_temp_path;
  };
  const handleNext = () => {
    if (validateForm()) {
      if (navigation && navigation.go) {
        navigation.go("awarding-feedback");
      }
    }
  };

  const handleBack = () => {
    if (navigation && navigation.previous) {
      navigation.previous();
    }
  };

  return (
    <div className="form-bg">
      <Container>
        <ToastContainer />
        <Row className="justify-content-md-center">
          <Col md={10}>
            <div className="form-main-header">
              <h4 className="form-title">Awarding Merienda</h4>
              <p className="form-desc">Please provide the merienda awarding details</p>
            </div>
            <div className="form-main">
              <div className="form-main-content">
                <Form>

                  {/* Upload photo of receipt (merienda purchase) */}
                  <Form.Group className="mb-4">
                    <Form.Label className="form-label">
                      Upload Photo of Receipt (Merienda Purchase) <span className="form-required">*</span>
                      {primaryDetails.merienda_purchase_receipt_temp_path && !isUploading && (
                        <span style={{ marginLeft: '10px', color: '#28a745' }}>✅ Uploaded</span>
                      )}
                    </Form.Label>
                    <Form.Control
                      type="file"
                      accept="image/*"
                      onChange={(e) => handlePhotoUpload(e, 'merienda_purchase_receipt')}
                      isInvalid={isError.merienda_purchase_receipt}
                      disabled={isUploading}
                    />
                    {isUploading && <div style={{ color: '#007bff', fontSize: '13px', marginTop: '6px' }}>⏳ Uploading...</div>}
                    {isError.merienda_purchase_receipt && (
                      <div className="form-error">Required</div>
                    )}
                  </Form.Group>

                  {/* Amount on receipt */}
                  <Form.Group className="mb-4" controlId="amount">
                    <Form.Label className="form-label">
                      Amount on Receipt <span className="form-required">*</span>
                    </Form.Label>
                    <Form.Control
                      type="text"
                      name="amount"
                      value={primaryDetails.amount || ""}
                      onChange={handleInputChange}
                      placeholder="Enter amount"
                      className={`form-input ${isError.amount ? "error" : ""}`}
                    />
                    {isError.amount && (
                      <div className="form-error">Please enter the amount on receipt.</div>
                    )}
                  </Form.Group>

                  {/* Food items */}
                  <Form.Group className="mb-4" controlId="food_item">
                    <Form.Label className="form-label">
                      Food Items <span className="form-required">*</span>
                    </Form.Label>
                    <Form.Control
                      type="text"
                      name="food_item"
                      value={primaryDetails.food_item || ""}
                      onChange={handleInputChange}
                      placeholder="Enter food items"
                      className={`form-input ${isError.food_item ? "error" : ""}`}
                    />
                    {isError.food_item && (
                      <div className="form-error">Please enter the food items.</div>
                    )}
                  </Form.Group>

                  {/* OR No. */}
                  <Form.Group className="mb-4" controlId="or_no">
                    <Form.Label className="form-label">
                      OR No. <span className="form-required">*</span>
                    </Form.Label>
                    <Form.Control
                      type="text"
                      name="or_no"
                      value={primaryDetails.or_no || ""}
                      onChange={handleInputChange}
                      placeholder="Enter OR number"
                      className={`form-input ${isError.or_no ? "error" : ""}`}
                    />
                    {isError.or_no && (
                      <div className="form-error">Please enter the OR number.</div>
                    )}
                  </Form.Group>

                  {/* Name of receiver */}
                  <Form.Group className="mb-4" controlId="receiver_name">
                    <Form.Label className="form-label">
                      Name of Receiver <span className="form-required">*</span>
                    </Form.Label>
                    <Form.Control
                      type="text"
                      name="receiver_name"
                      value={primaryDetails.receiver_name || ""}
                      onChange={handleInputChange}
                      placeholder="Enter receiver name"
                      className={`form-input ${isError.receiver_name ? "error" : ""}`}
                    />
                    {isError.receiver_name && (
                      <div className="form-error">Please enter the name of receiver.</div>
                    )}
                  </Form.Group>

                  {/* Position of receiver */}
                  <Form.Group className="mb-4" controlId="receiver_position">
                    <Form.Label className="form-label">
                      Position of Receiver <span className="form-required">*</span>
                    </Form.Label>
                    <Form.Control
                      type="text"
                      name="receiver_position"
                      value={primaryDetails.receiver_position || ""}
                      onChange={handleInputChange}
                      placeholder="Enter receiver position"
                      className={`form-input ${isError.receiver_position ? "error" : ""}`}
                    />
                    {isError.receiver_position && (
                      <div className="form-error">Please enter the position of receiver.</div>
                    )}
                  </Form.Group>

                  {/* Upload awarding photo */}
                  <Form.Group className="mb-4">
                    <Form.Label className="form-label">
                      Upload Awarding Photo (Certificate + Merienda) <span className="form-required">*</span>
                      {primaryDetails.merienda_certificate_temp_path && !isUploading && (
                        <span style={{ marginLeft: '10px', color: '#28a745' }}>✅ Uploaded</span>
                      )}
                    </Form.Label>
                    <Form.Control
                      type="file"
                      accept="image/*"
                      onChange={(e) => handlePhotoUpload(e, 'merienda_certificate')}
                      isInvalid={isError.merienda_certificate}
                      disabled={isUploading}
                    />
                    {isUploading && <div style={{ color: '#007bff', fontSize: '13px', marginTop: '6px' }}>⏳ Uploading...</div>}
                    {isError.merienda_certificate && (
                      <div className="form-error">Required</div>
                    )}
                  </Form.Group>

                  {/* Navigation Buttons */}
                  <div className="form-btn-row">
                    <AppButton
                      onClick={handleBack}
                      variant="back"
                      className="form-btn variant-secondary"
                    >
                      ← Back
                    </AppButton>
                    <AppButton
                      onClick={handleNext}
                      variant="next"
                      className="form-btn variant-primary"
                      disabled={!isFormValid()}
                    >
                      Next →
                    </AppButton>
                  </div>

                </Form>
              </div>
            </div>
          </Col>
        </Row>
      </Container>
    </div>
  );
}

export default AwardingMerienda;
